import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { clinicsService } from '@/services/api/clinics.service';
import { doctorsService } from '@/services/api/doctors.service';

type WizardStep = 'clinic' | 'doctor' | 'complete';

function SetupWizardPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<WizardStep>('clinic');
  const [loading, setLoading] = useState(false);

  // Clinic data
  const [clinicData, setClinicData] = useState({
    name: '',
    address: '',
    phone: '',
    cnpj: '',
  });
  const [clinicId, setClinicId] = useState<string>('');

  // Doctor data
  const [doctorData, setDoctorData] = useState({
    name: '',
    email: '',
    crm: '',
    specialty: '',
    phone: '',
  });

  const handleClinicSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!clinicData.name.trim()) {
      toast.error('Nome da clínica é obrigatório');
      return;
    }

    try {
      setLoading(true);
      const clinic = await clinicsService.create({
        ...clinicData,
        isActive: true,
      });
      setClinicId(clinic.id);
      toast.success('Clínica criada com sucesso');
      setCurrentStep('doctor');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao criar clínica');
    } finally {
      setLoading(false);
    }
  };

  const handleDoctorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!doctorData.name.trim()) {
      toast.error('Nome do médico é obrigatório');
      return;
    }

    try {
      setLoading(true);
      const doctor = await doctorsService.create({
        ...doctorData,
        isActive: true,
      });

      // Associate doctor with clinic
      await doctorsService.addToClinic(doctor.id, clinicId);

      toast.success('Médico criado com sucesso');
      setCurrentStep('complete');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao criar médico');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    navigate('/dashboard');
  };

  const handleSkipDoctor = () => {
    setCurrentStep('complete');
  };

  const getStepNumber = (step: WizardStep): number => {
    const steps: WizardStep[] = ['clinic', 'doctor', 'complete'];
    return steps.indexOf(step) + 1;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Passo {getStepNumber(currentStep)} de 3
            </span>
            <span className="text-sm font-medium text-gray-700">
              {Math.round((getStepNumber(currentStep) / 3) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(getStepNumber(currentStep) / 3) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          {currentStep === 'clinic' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Configure sua primeira clínica
              </h1>
              <p className="text-gray-600 mb-6">
                Adicione as informações da sua primeira clínica. Você poderá adicionar
                mais clínicas depois.
              </p>

              <form onSubmit={handleClinicSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome da Clínica *
                  </label>
                  <input
                    type="text"
                    value={clinicData.name}
                    onChange={(e) =>
                      setClinicData({ ...clinicData, name: e.target.value })
                    }
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Ex: Clínica Centro"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Endereço
                  </label>
                  <input
                    type="text"
                    value={clinicData.address}
                    onChange={(e) =>
                      setClinicData({ ...clinicData, address: e.target.value })
                    }
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Rua, número, bairro, cidade"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone
                    </label>
                    <input
                      type="text"
                      value={clinicData.phone}
                      onChange={(e) =>
                        setClinicData({ ...clinicData, phone: e.target.value })
                      }
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="(00) 0000-0000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CNPJ
                    </label>
                    <input
                      type="text"
                      value={clinicData.cnpj}
                      onChange={(e) =>
                        setClinicData({ ...clinicData, cnpj: e.target.value })
                      }
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="00.000.000/0000-00"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? 'Criando...' : 'Próximo'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {currentStep === 'doctor' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Adicione seu primeiro médico
              </h1>
              <p className="text-gray-600 mb-6">
                Cadastre o primeiro médico da sua clínica. Você poderá adicionar mais
                médicos depois.
              </p>

              <form onSubmit={handleDoctorSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Médico *
                  </label>
                  <input
                    type="text"
                    value={doctorData.name}
                    onChange={(e) =>
                      setDoctorData({ ...doctorData, name: e.target.value })
                    }
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Dr. João Silva"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={doctorData.email}
                    onChange={(e) =>
                      setDoctorData({ ...doctorData, email: e.target.value })
                    }
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="medico@email.com"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CRM
                    </label>
                    <input
                      type="text"
                      value={doctorData.crm}
                      onChange={(e) =>
                        setDoctorData({ ...doctorData, crm: e.target.value })
                      }
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="123456"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Especialidade
                    </label>
                    <input
                      type="text"
                      value={doctorData.specialty}
                      onChange={(e) =>
                        setDoctorData({ ...doctorData, specialty: e.target.value })
                      }
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="Cardiologia"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone
                  </label>
                  <input
                    type="text"
                    value={doctorData.phone}
                    onChange={(e) =>
                      setDoctorData({ ...doctorData, phone: e.target.value })
                    }
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <div className="flex justify-between pt-4">
                  <button
                    type="button"
                    onClick={() => setCurrentStep('clinic')}
                    className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                    disabled={loading}
                  >
                    Voltar
                  </button>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleSkipDoctor}
                      className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                      disabled={loading}
                    >
                      Pular
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      disabled={loading}
                    >
                      {loading ? 'Criando...' : 'Concluir'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {currentStep === 'complete' && (
            <div className="text-center py-8">
              <div className="mb-6">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>

              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Configuração Concluída!
              </h1>
              <p className="text-gray-600 mb-8">
                Sua organização e clínica foram configuradas com sucesso. Você já pode
                começar a usar o sistema.
              </p>

              <div className="space-y-3">
                <button
                  onClick={handleComplete}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  Ir para Dashboard
                </button>
                <p className="text-sm text-gray-500">
                  Você pode adicionar mais clínicas, médicos e usuários na área
                  administrativa.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Help Text */}
        {currentStep !== 'complete' && (
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-500">
              Precisa de ajuda? Entre em contato com o suporte.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SetupWizardPage;
